import { connect, ConnectedProps } from "react-redux";
import { RootState } from "../store/store";
import { useState } from "react";
import CustomPaginatedTable from "../components/CustomPaginatedTable";
import { useMutation } from "@tanstack/react-query";
import { queryApi } from "../store/actions/common";
import { triggerNotification } from "../store/directActions";
import moment from "moment";
import { getTagColor } from "../utils/getTagColor";
import { Button, Tag, Space } from "antd";
import { AppstoreOutlined, TableOutlined } from "@ant-design/icons";
import { useLocalState } from "../hooks/useLocalStorage";

interface OwnProps {}

type configurationPageProps = OwnProps & PropsFromRedux;

const PAGE_TYPE = "expense";
const VIEW_MODE_STORAGE_KEY = "shared-page-view-mode";

const ConfigurationPage: React.FC<configurationPageProps> = ({ storeId }) => {
  const [filterData, setFilterData] = useState({});
  const [viewMode, setViewMode] = useLocalState(
    VIEW_MODE_STORAGE_KEY,
    "card",
  );

  // Mutation to get payment URL
  const { mutate: getPaymentUrl, isPending: isPaymentLoading } = useMutation({
    mutationFn: (id: string) => queryApi(`store/payment/${id}`, "POST"),
    onSuccess: (data: any) => {
      if (data && data.payment_link) {
        window.location.href = data.payment_link;
      } else if (data && !data.status) {
        triggerNotification({
          title: "Error",
          description: data?.message || "Failed to get payment link",
          type: "error",
        });
      }
    },
    onError: (error: any) => {
      triggerNotification({
        title: "Error",
        description: error.message || "Failed to get payment link",
        type: "error",
      });
    },
  });

  const onPay = (id: string) => {
    if (id) {
      getPaymentUrl(id);
    }
  };

  const customRender = (item: any, index: number, globalIndex: number) => {
    return (
      <div
        key={item.payment_id || index}
        className="app-surface rounded-xl shadow-sm border border-solid app-border p-6 hover:shadow-md transition-shadow duration-200 mb-4"
      >
        <div className="flex items-start justify-between mb-4">
          <div className="flex-1">
            <div className="inline-flex items-center px-3 py-1 bg-blue-100 dark:bg-blue-900/30 rounded-lg mb-2">
              <span className="text-lg font-bold text-blue-600 dark:text-blue-400">
                {moment(item.created_at).format("MMMM DD, YYYY")}
              </span>
            </div>
            <p className="text-sm app-muted">
              Payment #{index + 1}
            </p>
          </div>
          <Tag color={getTagColor(item.payment_status)} className="text-sm">
            {item.payment_status}
          </Tag>
        </div>

        <div className="mb-4">
          <p className="text-sm app-muted">Amount</p>
          <p className="text-2xl font-bold"
          style={{ color: "var(--dash-value)" }}>
            ${item.amount}
          </p>
        </div>

        <div className="mb-4">
          <p className="text-sm app-muted">Updated at</p>
          <p className="text-sm font-medium app-text">
            {moment(item.updated_at).format("MMMM DD, YYYY")}
          </p>
        </div>

        <div className="flex justify-end items-center pt-4 border-0 border-t border-solid app-border">
          {item.payment_status === "pending" && (
            <Button
              type="primary"
              onClick={() => onPay(item.payment_id)}
              loading={isPaymentLoading}
              className="bg-blue-600 hover:bg-blue-700"
            >
              Pay
            </Button>
          )}
        </div>
      </div>
    );
  };

  const columns = [
    {
      title: "Bill date",
      dataIndex: "created_at",
      key: "created_at",
      sorter: (a: any, b: any) =>
        moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
      render: (item: any) => <div>{moment(item).format("MMMM DD, YYYY")}</div>,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      sorter: (a: any, b: any) => (a.amount ?? 0) - (b.amount ?? 0),
      render: (item: any) => <div>${item}</div>,
    },
    {
      title: "Status",
      key: "status",
      dataIndex: "payment_status",
      sorter: (a: any, b: any) =>
        (a.payment_status ?? "").localeCompare(b.payment_status ?? ""),
      render: (item: any) => <Tag color={getTagColor(item)}>{item}</Tag>,
    },
    {
      title: "Updated at",
      dataIndex: "updated_at",
      key: "updated_at",
      sorter: (a: any, b: any) =>
        moment(a.updated_at).valueOf() - moment(b.updated_at).valueOf(),
      render: (item: any) => <div>{moment(item).format("MMMM DD, YYYY")}</div>,
    },
    {
      title: "Action",
      key: "action",
      dataIndex: "payment_status",
      render: (item: any, record: any) =>
        item === "pending" && (
          <Button
            key="1"
            type="link"
            onClick={() => onPay(record.payment_id)}
            loading={isPaymentLoading}
          >
            Pay
          </Button>
        ),
    },
  ];

  return (
    <section id="customers" className="page-section h-page p-6 flex flex-col">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-2xl font-bold app-text"></h2>
        <Button.Group>
          <Button
            type={viewMode === "table" ? "primary" : "default"}
            icon={<TableOutlined />}
            onClick={() => setViewMode("table")}
          >
            Table
          </Button>
          <Button
            type={viewMode === "card" ? "primary" : "default"}
            icon={<AppstoreOutlined />}
            onClick={() => setViewMode("card")}
          >
            Card
          </Button>
        </Button.Group>
      </div>

      {/* @ts-ignore */}
      <CustomPaginatedTable
        columns={columns}
        showLimitChanger
        filterData={filterData}
        queryKey={[`/store/payment`, { store: storeId }]}
        path={`/store/payment/${storeId}`}
        customRender={viewMode === "card" ? customRender : null}
        customWrapperClass={
          viewMode === "card"
            ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            : undefined
        }
        tableProps={
          viewMode === "table"
            ? {
                sticky: true,
                tableLayout: "auto",
                scroll: { y: true, x: true },
              }
            : undefined
        }
      />
    </section>
  );
};

const mapStateToProps = (state: RootState) => ({
  // Map state properties to props here if needed
  lookUpData: state.cms?.lookup?.country?.[0]?.children ?? [],
  storeId: state.user?.profileData?.data?.store_id,
  REFRESH: state.levent?.[PAGE_TYPE],
});

const mapDispatchToProps = () => ({});

const connector = connect(mapStateToProps, mapDispatchToProps);

// Define the props that connect will inject into the component
type PropsFromRedux = ConnectedProps<typeof connector>;

// Connect the component to Redux
export default connector(ConfigurationPage);
